import React, { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { c } from 'ttag';

import { Alert, Button, Modal, ModalContent, ModalFooter, Textarea } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import {
    AccessibleAccountsDocument,
    CloseAccountToPortalDocument,
    OpenAccountToPortalDocument,
} from '../../graphql/__generated__/types';
import { refusalOf } from './portalAccess';

/** Which way the gesture goes. The two are **not** symmetric: see below. */
export type PortalAccessDirection = 'open' | 'close';

interface Props {
    accountId: string;
    accountName: string;
    direction: PortalAccessDirection;
    onClose: () => void;
}

/**
 * The confirmation of « ouvrir » and « fermer l'accès d'un compte au portail ».
 *
 * **Why a modal and not an inline toggle.** Both directions change a fact about a
 * client, recorded in the audit trail, and neither is undone by clicking again:
 * re-closing a freshly opened account refuses as soon as somebody has been invited.
 * The design system's `Modal` is what already carries a consequential confirmation,
 * so the gesture composes it rather than inventing a look.
 *
 * **Why the two directions share one component while reading differently.** They act
 * on the same field, so splitting them would have duplicated the mutation plumbing
 * and the refusal reading; but the *wording* is deliberately asymmetric, because the
 * gestures are:
 *
 * - opening makes the account **eligible** to receive portal users. It grants access
 *   to nobody, and the sentence saying so is the one thing this dialog must not lose:
 *   an administrator who reads « ouvrir » as « donner un accès » spends the afternoon
 *   looking for whom they gave it to;
 * - closing takes nobody's access away either: `portal_enabled` is read by no
 *   reading path, which is why the API refuses while a membership is still alive
 *   rather than producing an account displayed as closed whose users keep signing in.
 *
 * The reason is **typed, never guessed**: it is journalled, and a motive invented by
 * the screen is worse in an audit log than no motive at all.
 */
const PortalAccessDialog = ({ accountId, accountName, direction, onClose }: Props) => {
    const [reason, setReason] = useState('');
    const opening = direction === 'open';

    /**
     * `AccountPortalAccess` and `AccessibleAccount` are two types over the same row,
     * so Apollo normalises them under two different keys and the mutation result can
     * never update the list on its own. Refetching is what makes the onboarding tree
     * stop greying the account: the loop this lot is about.
     */
    const options = { refetchQueries: [AccessibleAccountsDocument], awaitRefetchQueries: true };
    const [openAccount, openState] = useMutation(OpenAccountToPortalDocument, options);
    const [closeAccount, closeState] = useMutation(CloseAccountToPortalDocument, options);

    const { loading, error } = opening ? openState : closeState;
    const done = opening ? openState.data?.openAccountToPortal : closeState.data?.closeAccountToPortal;

    const submit = () => {
        const run = opening
            ? openAccount({ variables: { organizationId: accountId } })
            : closeAccount({ variables: { organizationId: accountId, reason: reason.trim() } });
        // The refusal is read off `error` below; without this the rejected promise
        // reaches the console as an unhandled one on every single refusal.
        run.catch(() => {});
    };

    const title = opening
        ? c('Portal access').t`Open this account to the portal`
        : c('Portal access').t`Close this account to the portal`;

    // A motive is required on screen although the API takes it as optional: it is
    // the only trace of *why*, and nobody reconstructs it six months later.
    const blocked = !opening && reason.trim().length === 0;

    return (
        <Modal open onClose={onClose} title={title} size={560}>
            <ModalContent>
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-text-900">{accountName}</p>

                    {done ? (
                        <Alert type="success">
                            {done.portalEnabled
                                ? c('Portal access')
                                      .t`The account is now open to the portal. Nobody has an access yet: onboard the people who should get one, each with their role and their perimeter.`
                                : c('Portal access')
                                      .t`The account is closed to the portal. No new invitation can be sent on it, and no access was taken away — there was none left to take.`}
                        </Alert>
                    ) : (
                        <>
                            {/* The whole point of the screen. Said before the button, not
                                after: a warning read on the confirmation comes too late. */}
                            <Alert type="warning">
                                {opening
                                    ? c('Portal access')
                                          .t`Opening grants an access to nobody. It makes the account eligible to receive portal users; who actually enters is decided afterwards, membership by membership, by an invitation carrying its own role and perimeter.`
                                    : c('Portal access')
                                          .t`Closing takes nobody's access away. It stops new invitations on this account; the people already attached keep signing in until their membership is revoked, one by one.`}
                            </Alert>

                            {!opening && (
                                <div>
                                    <label
                                        htmlFor="portal-access-reason"
                                        className="mb-1.5 block text-sm font-medium text-text-900"
                                    >
                                        {c('Portal access').t`Reason for closing`}
                                    </label>
                                    <Textarea
                                        id="portal-access-reason"
                                        value={reason}
                                        onValue={setReason}
                                        placeholder={c('Portal access')
                                            .t`Contract ended, account merged into another one…`}
                                    />
                                    <p className="mt-1.5 text-xs text-text-600">
                                        {c('Portal access')
                                            .t`It is journalled with the gesture, and it is the only trace of why the account was closed.`}
                                    </p>
                                </div>
                            )}

                            {error && <RefusalNotice refusal={refusalOf(error)} />}
                        </>
                    )}
                </div>
            </ModalContent>

            <ModalFooter>
                {done ? (
                    // « Terminé » and not « Fermer »: on this dialog of all places,
                    // « Fermer » is the name of the other gesture. The button that
                    // dismisses a confirmation must not read like the action itself.
                    <Button category="default" onClick={onClose}>
                        {c('Portal access action').t`Done`}
                    </Button>
                ) : (
                    <>
                        <Button category="weak" onClick={onClose}>
                            {c('Action').t`Cancel`}
                        </Button>
                        <Button
                            // `destructive` for the closing only: opening a client account
                            // is consequential, not destructive, and a red button would
                            // reinforce the very reading the warning above is fighting.
                            category={opening ? 'default' : 'destructive'}
                            loading={loading}
                            disabled={blocked}
                            onClick={submit}
                        >
                            {opening
                                ? c('Portal access action').t`Open the account`
                                : c('Portal access action').t`Close the account`}
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default PortalAccessDialog;
